import { Children, isValidElement, type ComponentPropsWithoutRef, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import {
  Link as LinkIcon,
  Info,
  FileText,
  AlertTriangle,
  Lightbulb,
  AlertOctagon,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DocsRendererProps {
  /** Raw markdown string to render */
  content: string;
  /** Optional class name for the container */
  className?: string;
}

// ---------------------------------------------------------------------------
// Custom heading renderer — adds anchor links for scroll-to navigation
// ---------------------------------------------------------------------------

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

type HeadingLevel = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

function createHeadingRenderer(level: HeadingLevel) {
  const Tag = level;
  return function HeadingWithAnchor(
    props: ComponentPropsWithoutRef<typeof Tag>,
  ) {
    const children = props.children;
    const text =
      typeof children === "string"
        ? children
        : Array.isArray(children)
          ? children
              .map((child) => (typeof child === "string" ? child : ""))
              .join("")
          : "";
    const id = slugify(text);

    return (
      <Tag id={id} className="group relative scroll-mt-20" {...props}>
        <a
          href={`#${id}`}
          className="absolute -left-6 top-1/2 hidden -translate-y-1/2 text-ev-text-muted opacity-0 transition-opacity group-hover:opacity-100 lg:inline-block"
          aria-label={`Link to ${text}`}
        >
          <LinkIcon className="h-4 w-4" />
        </a>
        {children}
      </Tag>
    );
  };
}

// ---------------------------------------------------------------------------
// Admonition (Confluence panel) renderer
// ---------------------------------------------------------------------------
//
// Backend converts Confluence info/note/warning/tip/panel macros into
// GitHub-style admonition blockquotes:
//
//     > [!INFO]
//     > content...
//
// Detect the `[!TYPE]` marker in a blockquote's first paragraph and render
// a colored panel with an icon. Colors match Confluence's intent so content
// creators see their macro selection reflected in the portal.

type AdmonitionType = "INFO" | "NOTE" | "WARNING" | "TIP" | "IMPORTANT";

const ADMONITION_STYLES: Record<
  AdmonitionType,
  { icon: typeof Info; label: string; classes: string; labelClasses: string }
> = {
  INFO: {
    icon: Info,
    label: "Info",
    classes: "border-blue-300 bg-blue-50 text-blue-950",
    labelClasses: "text-blue-900",
  },
  NOTE: {
    icon: FileText,
    label: "Note",
    classes: "border-slate-300 bg-slate-50 text-slate-900",
    labelClasses: "text-slate-800",
  },
  WARNING: {
    icon: AlertTriangle,
    label: "Warning",
    classes: "border-amber-300 bg-amber-50 text-amber-950",
    labelClasses: "text-amber-900",
  },
  TIP: {
    icon: Lightbulb,
    label: "Tip",
    classes: "border-emerald-300 bg-emerald-50 text-emerald-950",
    labelClasses: "text-emerald-900",
  },
  IMPORTANT: {
    icon: AlertOctagon,
    label: "Important",
    classes: "border-orange-300 bg-orange-50 text-orange-950",
    labelClasses: "text-orange-900",
  },
};

/** Extract flat text from React children so we can match the admonition marker. */
function extractText(node: ReactNode): string {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(extractText).join("");
  if (isValidElement<{ children?: ReactNode }>(node)) {
    return extractText(node.props.children);
  }
  return "";
}

const ADMONITION_MARKER_RE = /^\[!(INFO|NOTE|WARNING|TIP|IMPORTANT)\]\s*/;

// Strip the `[!TYPE]` marker from the start of a string, recursively descending
// into ReactMarkdown's tree. Used when the marker shares a paragraph with the
// first line of content (e.g. `> [!INFO]\n> content` renders as a single <p>
// with "[!INFO]\ncontent" — we remove the marker so only the body shows).
function stripMarkerFromFirstText(node: ReactNode): ReactNode {
  if (typeof node === "string") {
    return node.replace(ADMONITION_MARKER_RE, "").replace(/^\n+/, "");
  }
  if (Array.isArray(node)) {
    const out: ReactNode[] = [];
    let stripped = false;
    for (const child of node) {
      if (!stripped) {
        const text = extractText(child);
        if (text.length > 0) {
          out.push(stripMarkerFromFirstText(child));
          stripped = true;
          continue;
        }
      }
      out.push(child);
    }
    return out;
  }
  if (isValidElement<{ children?: ReactNode }>(node)) {
    return {
      ...node,
      props: {
        ...node.props,
        children: stripMarkerFromFirstText(node.props.children),
      },
    } as typeof node;
  }
  return node;
}

function CustomBlockquote(props: ComponentPropsWithoutRef<"blockquote">) {
  const { children, ...rest } = props;
  const childArray = Children.toArray(children).filter(
    (c) => !(typeof c === "string" && c.trim() === ""),
  );

  // Check whether the first non-empty child starts with `[!TYPE]`. The marker
  // may be its own paragraph (two-paragraph form) or share a paragraph with
  // the first line of content (single-paragraph form from `> [!INFO]\n> content`
  // without a blank line). Handle both.
  const firstText = extractText(childArray[0]).trim();
  const match = firstText.match(ADMONITION_MARKER_RE);

  if (!match) {
    return <blockquote {...rest}>{children}</blockquote>;
  }

  const type = match[1] as AdmonitionType;
  const style = ADMONITION_STYLES[type];
  const Icon = style.icon;

  // If the entire first paragraph was just the marker, drop it and use the
  // remaining children as the body. Otherwise strip the marker from the first
  // paragraph's text and keep that paragraph (now holding only the body text).
  const markerIsWholeParagraph = firstText === match[0].trim();
  const body = markerIsWholeParagraph
    ? childArray.slice(1)
    : [stripMarkerFromFirstText(childArray[0]), ...childArray.slice(1)];

  return (
    <aside
      className={`my-4 rounded-ev border-l-4 px-4 py-3 ${style.classes}`}
      role="note"
      aria-label={style.label}
    >
      <div className={`mb-1 flex items-center gap-2 text-sm font-semibold ${style.labelClasses}`}>
        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        {style.label}
      </div>
      <div className="text-sm leading-relaxed [&>p]:m-0 [&>p+p]:mt-2 [&_strong]:font-semibold">
        {body}
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Custom link renderer — opens external links (and file downloads) in new tab
// ---------------------------------------------------------------------------

// Pages embed inside an OnRamp iframe (see the app's frame-ancestors CSP).
// The external-proxy download link is a same-origin relative path, so the
// `http`/`//` check below doesn't catch it — without this, clicking it
// navigates the embedding iframe itself to the raw (Content-Disposition:
// inline) file stream instead of opening it in its own tab, which reads to
// the customer as "the download doesn't work" (it-ops-context — docs hub
// download link investigation).
const FILE_DOWNLOAD_PREFIX = "/api/docs/external-proxy/";

function CustomLink(props: ComponentPropsWithoutRef<"a">) {
  const { href, children, ...rest } = props;
  const opensInNewTab =
    href?.startsWith("http") ||
    href?.startsWith("//") ||
    href?.startsWith(FILE_DOWNLOAD_PREFIX);

  return (
    <a
      href={href}
      {...(opensInNewTab
        ? { target: "_blank", rel: "noopener noreferrer" }
        : {})}
      {...rest}
    >
      {children}
    </a>
  );
}

// ---------------------------------------------------------------------------
// DocsRenderer
// ---------------------------------------------------------------------------

/**
 * Renders markdown content using react-markdown with GFM support,
 * XSS sanitization, and Tailwind Typography prose styling.
 *
 * Features:
 * - GFM: tables, task lists, strikethrough, autolinks
 * - Sanitized HTML (no script execution via rehype-sanitize)
 * - Custom heading renderer with scroll-to-anchor links
 * - External links open in new tabs
 * - Tailwind Typography prose classes for readable typography
 */
export function DocsRenderer({ content, className }: DocsRendererProps) {
  return (
    <div
      className={[
        "prose prose-gray max-w-none",
        // Heading styles
        "prose-headings:text-ev-text-primary prose-headings:font-semibold",
        // Body text
        "prose-p:text-ev-text-primary prose-p:leading-relaxed",
        // Links
        "prose-a:text-ev-info-text prose-a:no-underline hover:prose-a:underline",
        // Code blocks
        "prose-code:rounded prose-code:bg-ev-surface-subtle prose-code:px-1.5 prose-code:py-0.5 prose-code:text-sm prose-code:font-normal",
        "prose-pre:rounded-ev prose-pre:border prose-pre:border-ev-border prose-pre:bg-ev-surface-subtle",
        // Tables
        "prose-table:border-collapse",
        "prose-th:border prose-th:border-ev-border prose-th:bg-ev-surface-subtle prose-th:px-4 prose-th:py-2 prose-th:text-left prose-th:font-semibold prose-th:text-ev-text-primary",
        "prose-td:border prose-td:border-ev-border prose-td:px-4 prose-td:py-2 prose-td:text-ev-text-primary",
        // Lists
        "prose-li:text-ev-text-primary",
        // Nested ordered-list numbering — match Confluence's editor convention:
        // depth 1 = 1,2,3 / depth 2 = a,b,c / depth 3 = i,ii,iii / depth 4 = 1,2,3 (cycles).
        // Confluence's storage format strips list-style hints (XHTML `<ol>` only carries
        // `start=` and `local-id=`), so we restore the convention via CSS based on depth.
        // Markdownify → ReactMarkdown produces plain nested <ol>, so descendant selectors work.
        "[&_ol_ol]:list-[lower-alpha]",
        "[&_ol_ol_ol]:list-[lower-roman]",
        "[&_ol_ol_ol_ol]:list-[decimal]",
        // Blockquotes
        "prose-blockquote:border-l-ev-info-text prose-blockquote:text-ev-text-secondary",
        // Horizontal rules
        "prose-hr:border-ev-border",
        // Strong/emphasis
        "prose-strong:text-ev-text-primary",
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[
          rehypeRaw,
          [rehypeSanitize, {
            ...defaultSchema,
            tagNames: [...(defaultSchema.tagNames ?? []), "img"],
            attributes: {
              ...defaultSchema.attributes,
              img: ["src", "alt", "width", "height"],
            },
          }],
        ]}
        components={{
          h1: createHeadingRenderer("h1"),
          h2: createHeadingRenderer("h2"),
          h3: createHeadingRenderer("h3"),
          h4: createHeadingRenderer("h4"),
          h5: createHeadingRenderer("h5"),
          h6: createHeadingRenderer("h6"),
          a: CustomLink,
          blockquote: CustomBlockquote,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
